import React, { useState, useRef } from "react";
import WebLayout from "../components/WebLayout";
import { toast } from "sonner";
import { CheckCircle2, Layers, X, Plus, AlertCircle, Loader2 } from "lucide-react";
import TurnstileWidget from "../components/TurnstileWidget";
import Seo from "../components/Seo";

const MACHINE_TYPES = ["Snack", "Drink", "Combo", "Smart Cooler", "Coffee", "Other"];
const LOCATION_TYPES = ["Warehouses", "Manufacturing", "Offices", "Hotels", "Apartments", "Gyms", "Trucking/Logistics", "Schools", "Healthcare", "Other"];
const API = process.env.REACT_APP_BACKEND_URL || "";
function readCookie(name) {
  try { return decodeURIComponent(document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] || ""); } catch { return ""; }
}

export default function OperatorsLanding() {
  const [form, setForm] = useState({
    fullName: "", company: "", email: "", phone: "", zip: "", radius: "50",
    states: [], machines: [], locations: [], minPeople: "50", website: "", website_hp: "",
  });
  const [newState, setNewState] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [consent, setConsent] = useState(false);
  const tsRef = useRef(null);
  const tsRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k, v) => {
    const cur = form[k];
    setForm({ ...form, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] });
  };
  const addState = () => {
    if (newState.trim() && !form.states.includes(newState.trim().toUpperCase())) {
      setForm({ ...form, states: [...form.states, newState.trim().toUpperCase()] });
      setNewState("");
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (tsRequired && !tsToken) {
      setBanner("Please complete the verification challenge and try again.");
      return;
    }
    // Auto-commit any un-added state chip so silent-drop doesn't happen on submit.
    const staged = newState.trim().toUpperCase();
    const effectiveStates = staged && !form.states.includes(staged)
      ? [...form.states, staged]
      : form.states;
    if (effectiveStates !== form.states) {
      setForm({ ...form, states: effectiveStates });
      setNewState("");
    }
    setLoading(true); setBanner("");
    try {
      const r = await fetch(`${API}/api/preregister/operator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.fullName, company: form.company, email: form.email, phone: form.phone,
          zip: form.zip, service_radius_miles: form.radius ? parseInt(form.radius, 10) : null,
          states_served: effectiveStates, machine_types: form.machines,
          preferred_location_types: form.locations,
          min_people: form.minPeople ? parseInt(form.minPeople, 10) : null,
          website: form.website || null,
          referral_code: readCookie("sb_ref") || null,
          website_hp: form.website_hp,
          turnstile_token: tsToken,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.status === "created") { setSubmitted(true); toast.success("Operator early access request received."); }
      else if (r.status === 200 && data.status === "already_registered") { setSubmitted(true); setAlreadyRegistered(true); toast.success("Already on the list."); }
      else if (r.status === 422 && data.status === "turnstile_failed") { setBanner("Verification failed. Please try again."); setTsToken(""); tsRef.current?.reset(); }
      else if (r.status === 422) { setBanner("Please review the form and try again."); setTsToken(""); tsRef.current?.reset(); }
      else if (r.status === 429) { setBanner("Too many attempts. Please try again later."); setTsToken(""); tsRef.current?.reset(); }
      else { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    } catch { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    finally { setLoading(false); }
  }

  return (
    <WebLayout>
      <Seo title="For Operators" description="Join SpotBounty's Operator early access — reserve your territory and get access to verified vending opportunities." path="/operators" />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1fr_1.1fr] gap-14">
        <div>
          <div className="sb-eyebrow flex items-center gap-2">
            <Layers size={14} className="text-[#FFC107]" /> For Operators
          </div>
          <h1 className="sb-headline mt-5 text-[48px] md:text-[64px]">
            VERIFIED SPOTS. <br /><span className="text-[#FFC107]">ZERO COLD CALLS.</span>
          </h1>
          <p className="mt-5 text-[#A1A1AA] max-w-lg leading-relaxed">
            Access a curated pipeline of decision-maker-verified vending opportunities. Filter by territory, machine type,
            and traffic. Unlock the ones that fit your route.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {[
              { v: "94%", l: "Verification Accuracy" },
              { v: "7-Day", l: "Buyer Protection" },
              { v: "42%", l: "Avg. Close Rate" },
            ].map((s) => (
              <div key={s.l} className="sb-card p-4">
                <div className="text-[24px] font-black">{s.v}</div>
                <div className="sb-eyebrow mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {!submitted ? (
            <form
              data-testid="operator-waitlist-form"
              onSubmit={handleSubmit}
              className="sb-card p-6 md:p-8 space-y-4"
            >
              <div>
                <div className="sb-eyebrow">Early Access</div>
                <h2 className="text-[28px] font-black mt-2">Reserve your territory.</h2>
              </div>
              {banner && (
                <div className="sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }}>
                  <AlertCircle size={14} className="text-[#EF4444] mt-0.5" />
                  <span className="text-[12.5px] text-[#EF4444]">{banner}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">Full Name</label><input required data-testid="op-fullname" className="sb-input" value={form.fullName} onChange={set("fullName")} /></div>
                <div><label className="sb-label">Company Name</label><input required data-testid="op-company" className="sb-input" value={form.company} onChange={set("company")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">Email</label><input required type="email" data-testid="op-email" className="sb-input" value={form.email} onChange={set("email")} /></div>
                <div><label className="sb-label">Mobile Phone</label><input required data-testid="op-phone" className="sb-input" value={form.phone} onChange={set("phone")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">ZIP</label><input required data-testid="op-zip" className="sb-input" value={form.zip} onChange={set("zip")} /></div>
                <div><label className="sb-label">Service Radius (miles)</label><input type="number" data-testid="op-radius" className="sb-input" value={form.radius} onChange={set("radius")} /></div>
              </div>

              <div>
                <label className="sb-label">States Served</label>
                <div className="flex gap-2">
                  <input value={newState} onChange={(e) => setNewState(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addState(); } }} onBlur={addState} placeholder="e.g. IL" className="sb-input" maxLength={2} data-testid="op-newstate" />
                  <button type="button" onClick={addState} className="sb-btn-secondary" style={{ padding: "10px 14px" }}><Plus size={16} /></button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {form.states.map((s) => (
                    <span key={s} className="sb-pill sb-pill-gold">
                      {s}
                      <button type="button" onClick={() => toggle("states", s)} className="ml-1"><X size={11} /></button>
                    </span>
                  ))}
                  {form.states.length === 0 && <span className="text-[12px] text-[#52525b]">Add one or more states…</span>}
                </div>
              </div>

              <div>
                <label className="sb-label">Machine Types</label>
                <div className="flex flex-wrap gap-2">
                  {MACHINE_TYPES.map((m) => {
                    const active = form.machines.includes(m);
                    return (
                      <button key={m} type="button" onClick={() => toggle("machines", m)} data-testid={`op-machine-${m}`} className={`sb-pill ${active ? "sb-pill-gold" : "sb-pill-gray"}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="sb-label">Preferred Location Types</label>
                <div className="flex flex-wrap gap-2">
                  {LOCATION_TYPES.map((m) => {
                    const active = form.locations.includes(m);
                    return (
                      <button key={m} type="button" onClick={() => toggle("locations", m)} data-testid={`op-loc-${m}`} className={`sb-pill ${active ? "sb-pill-gold" : "sb-pill-gray"}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">Min. Preferred People</label><input type="number" data-testid="op-minpeople" className="sb-input" value={form.minPeople} onChange={set("minPeople")} /></div>
                <div><label className="sb-label">Website (optional)</label><input data-testid="op-website" className="sb-input" value={form.website} onChange={set("website")} /></div>
              </div>

              <TurnstileWidget ref={tsRef} onToken={setTsToken} testId="op-turnstile" />

              <label htmlFor="op-consent" className="flex items-start gap-2 text-[12px] text-[#A1A1AA] leading-relaxed cursor-pointer">
                <input id="op-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="op-consent" className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0" required />
                <span>I agree to the <a href="/terms" className="text-[#FFC107] underline">Terms of Service</a> and acknowledge the <a href="/privacy" className="text-[#FFC107] underline">Privacy Policy</a>.</span>
              </label>
              <button type="submit" disabled={loading || (tsRequired && !tsToken) || !consent} data-testid="op-submit" className="sb-btn-primary w-full disabled:opacity-50">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Join Operator Early Access"}
              </button>
              <input type="text" name="website_hp" value={form.website_hp} onChange={set("website_hp")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            </form>
          ) : (
            <div data-testid="op-confirmation" className="sb-card p-10 text-center">
              <CheckCircle2 size={48} className="text-[#FFC107] mx-auto" />
              <div className="mt-5 sb-eyebrow">Confirmation</div>
              <h2 className="text-[32px] font-black mt-3">YOU'RE ON THE OPERATOR LIST.</h2>
              <p className="mt-4 text-[#A1A1AA] max-w-md mx-auto">
                {alreadyRegistered
                  ? "You're already on the list — thanks for reconfirming your interest."
                  : "We'll use your preferred territory to help guide future SpotBounty Scout campaigns."}
              </p>
            </div>
          )}
        </div>
      </section>
    </WebLayout>
  );
}
